import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Radio, Select, Switch, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../../components/ActionTooltip.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"
import { useTemplateBlocks } from "../../../hooks/use-template-blocks.ts"
import {
  SIGNATURE_FIELD_TYPES,
  type SignerSlot,
  declaredSlots
} from "../../../signature-slots/signature-slots.ts"
import type { TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"

const { Text } = Typography

/**
 * Column widths shared by the header row and every field row, so the two line
 * up; the rows scroll sideways inside the block card rather than reflowing
 * away from the header they are labelled by.
 */
const ROWS_MIN_WIDTH_CLASS = "min-w-[38rem]"
const SLOT_PICKER_CLASS = "w-[16rem]"
const FIELD_TYPE_COLUMN_CLASS = "w-[16rem]"
const LABEL_COLUMN_CLASS = "min-w-[10rem] flex-1"
const REQUIRED_COLUMN_CLASS = "w-[9rem]"
const ACTION_COLUMN_CLASS = "w-8"

interface SignatureFieldsEditorProps {
  form: FormInstance<TemplateDocumentFormData>
  index: number
  disabled?: boolean
}

/**
 * A field nobody has to complete is not much of a signature, so a new field
 * starts out required and an author opts out of that deliberately.
 *
 * `true` is also what the backend assumes for a field that arrives without
 * the key at all — `SignatureField.required` defaults to `True`, and
 * `slot_mapping` reads `field.get("required", True)` — so defaulting to
 * `false` here made the builder the one place that produced optional
 * fields, for an author who never touched the switch.
 */
function newField() {
  return {
    key: crypto.randomUUID(),
    field_type: SIGNATURE_FIELD_TYPES[0],
    label: "",
    required: true
  }
}

/**
 * The rest of a signature block's story, under the `signer_label` caption:
 * which declared slot signs it, and the fields that slot's holder fills in.
 *
 * Both are read from and written to the block's own `config` (`slot`,
 * `fields`) — the same structure `signature-slots.ts` and the Signers panel
 * already agree on — so a block never invents its own copy of a signer. The
 * slot Select stores the whole `SignerSlot`, not just its key, because the
 * backend requires every block sharing a key to describe that person the
 * same way (name, party type included).
 */
export function SignatureFieldsEditor({
  form,
  index,
  disabled
}: SignatureFieldsEditorProps) {
  const { t } = useTranslation()
  const blocks = useTemplateBlocks(form)
  const slots = declaredSlots(blocks)
  const slot = Form.useWatch(["blocks", index, "config", "slot"], form) as
    | SignerSlot
    | undefined

  /**
   * Unbinding a block from its signer takes that signer's fields with it.
   * A field only means anything as something the slot's holder completes, so
   * leaving them behind would strand them: nothing downstream can place
   * them, and the save is refused until the author deletes each one by hand.
   * This mirrors `blocksWithSlotRemoved`, which is what the Signers panel
   * already does to every block when the signer itself is removed.
   */
  const clearSlot = () => {
    form.setFields([
      {
        name: ["blocks", index, "config", "slot"],
        touched: true,
        value: undefined
      },
      {
        name: ["blocks", index, "config", "fields"],
        touched: true,
        value: undefined
      }
    ])
  }

  return (
    <Flex vertical gap={8}>
      <Flex gap={8} align="end" wrap>
        <Form.Item
          name={["blocks", index, "config", "slot"]}
          label={t("documentTemplates.signers.slotLabel")}
          className={`mb-0 ${SLOT_PICKER_CLASS}`}
          getValueProps={(value: SignerSlot | undefined) => ({
            value: value?.key
          })}
          getValueFromEvent={(key: string | undefined) =>
            (key && slots.find((candidate) => candidate.key === key)) ||
            undefined
          }
        >
          <Select
            disabled={disabled}
            aria-label={t("documentTemplates.signers.slotLabel")}
            placeholder={t("documentTemplates.signers.slotPlaceholder")}
            options={slots.map((candidate) => ({
              value: candidate.key,
              label: candidate.name
            }))}
          />
        </Form.Item>
        {/* Its own button rather than the Select's `allowClear` cross:
            rc-select renders that cross `aria-hidden` and opens it on
            mousedown, so a keyboard user could never unbind the block. */}
        {slot && (
          <Button
            type="link"
            size="small"
            disabled={disabled}
            onClick={clearSlot}
          >
            {t("documentTemplates.signers.clearSlotButton")}
          </Button>
        )}
      </Flex>

      <Text strong>{t("documentTemplates.signers.fieldsTitle")}</Text>

      <Form.List name={["blocks", index, "config", "fields"]}>
        {(fields, { add, remove }) => (
          <Flex vertical gap={8}>
            {fields.length > 0 && (
              <div className="overflow-x-auto tailwind-scroll">
                <Flex vertical gap={8} className={ROWS_MIN_WIDTH_CLASS}>
                  <Flex gap={8} align="center">
                    <Text type="secondary" className={FIELD_TYPE_COLUMN_CLASS}>
                      {t("documentTemplates.signers.fieldTypeLabel")}
                    </Text>
                    <Text type="secondary" className={LABEL_COLUMN_CLASS}>
                      {t("documentTemplates.signers.fieldLabel")}
                      <span aria-hidden className="ml-0.5 text-red-500">
                        *
                      </span>
                    </Text>
                    <span className={REQUIRED_COLUMN_CLASS} />
                    <span className={ACTION_COLUMN_CLASS} />
                  </Flex>

                  {fields.map((field) => (
                    <Flex key={field.key} gap={8} align="start">
                      <Form.Item name={[field.name, "field_type"]} noStyle>
                        <Radio.Group
                          // One native radio group per row: an inherited name
                          // is a single group, and choosing a type in one row
                          // would clear another's.
                          name={`field-type-${index}-${field.key}`}
                          disabled={disabled}
                          className={FIELD_TYPE_COLUMN_CLASS}
                          aria-label={t(
                            "documentTemplates.signers.fieldTypeLabel"
                          )}
                          options={SIGNATURE_FIELD_TYPES.map((type) => ({
                            value: type,
                            label: t(
                              `documentTemplates.signers.fieldType.${type}`
                            )
                          }))}
                        />
                      </Form.Item>

                      {/* The label is what the signer reads next to the line
                          they fill in, and the backend renders it verbatim —
                          an empty one puts an unexplained blank line on the
                          signed PDF. Not `noStyle`, so the message actually
                          has somewhere to appear. */}
                      <Form.Item
                        name={[field.name, "label"]}
                        className={`mb-0 ${LABEL_COLUMN_CLASS}`}
                        required
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: t(
                              "documentTemplates.signers.fieldLabelRequired"
                            )
                          }
                        ]}
                      >
                        <TextInput
                          disabled={disabled}
                          aria-label={t("documentTemplates.signers.fieldLabel")}
                          aria-required
                          placeholder={t(
                            "documentTemplates.signers.fieldLabelPlaceholder"
                          )}
                        />
                      </Form.Item>

                      <Flex
                        gap={6}
                        align="center"
                        className={REQUIRED_COLUMN_CLASS}
                      >
                        <Form.Item
                          name={[field.name, "required"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch
                            disabled={disabled}
                            aria-label={t(
                              "documentTemplates.signers.fieldRequiredLabel"
                            )}
                          />
                        </Form.Item>
                        <Text>
                          {t("documentTemplates.signers.fieldRequiredLabel")}
                        </Text>
                      </Flex>

                      <ActionTooltip
                        title={t(
                          "documentTemplates.signers.removeFieldButtonLabel"
                        )}
                      >
                        <Button
                          aria-label={t(
                            "documentTemplates.signers.removeFieldButtonLabel"
                          )}
                          danger
                          type="text"
                          disabled={disabled}
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </ActionTooltip>
                    </Flex>
                  ))}
                </Flex>
              </div>
            )}

            <Flex gap={8} align="center">
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                disabled={disabled || !slot}
                onClick={() => add(newField())}
              >
                {t("documentTemplates.signers.addFieldButton")}
              </Button>
              {!slot && (
                <Text type="secondary">
                  {t("documentTemplates.signers.noSlotHint")}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      </Form.List>
    </Flex>
  )
}
